import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Provider } from 'react-redux';
import { store } from './store/store';
import 'dayjs/locale/it';

import { useSelector } from 'react-redux';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clienti from './pages/Clienti';
import ClienteDettaglio from './pages/ClienteDettaglio';
import Cantieri from './pages/Cantieri';
import CantiereDettaglio from './pages/CantiereDettaglio';
import Interventi from './pages/Interventi';
import InterventoDettaglio from './pages/InterventoDettaglio';
import Calendario from './pages/Calendario';
import Timbrature from './pages/Timbrature';
import Rapportini from './pages/Rapportini';
import Utenti from './pages/Utenti';
import Veicoli from './pages/Veicoli';
import Manuale from './pages/Manuale';
import Profilo from './pages/Profilo';
import Impostazioni from './pages/Impostazioni';
import RegistroAttivita from './pages/RegistroAttivita';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import SyncManager from './components/SyncManager';

// Componente per il redirect iniziale basato sul ruolo
const RoleBasedRedirect = () => {
  const { user } = useSelector((state) => state.auth);
  // Admin -> Dashboard, Capocantiere e Tecnico -> Calendario
  const redirectPath = (user?.role === 'admin' || user?.role === 'amministratore') ? '/dashboard' : '/calendario';
  return <Navigate to={redirectPath} replace />;
};

// Componente per proteggere la Dashboard (solo amministratore)
const DashboardProtected = () => {
  const { user } = useSelector((state) => state.auth);

  if (user?.role !== 'admin' && user?.role !== 'amministratore') {
    return <Navigate to="/calendario" replace />;
  }

  return <Dashboard />;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#fd0000',
      dark: '#b00000',
      light: '#ff4444',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    error: {
      main: '#fd0000',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    // --- Bersagli tattili ---------------------------------------------------
    // Su telefono e tablet diverse azioni (Visualizza / Modifica / Elimina) erano
    // pulsanti da 28-30px, sotto la soglia di ~44px: difficili da centrare e con
    // "Elimina" adiacente alle altre. La regola vale sui dispositivi touch
    // (pointer: coarse), non per larghezza: così copre anche il tablet in
    // orizzontale e lascia il desktop col mouse esattamente com'è.
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (pointer: coarse)': {
            minWidth: 44,
            minHeight: 44,
            // Dentro i campi (freccia della Select, "Open" di Autocomplete, occhio
            // della password) 40x40: stanno comodi nei campi alti 48 definiti qui
            // sotto, senza deformarli.
            '.MuiInputBase-root &': { minWidth: 40, minHeight: 40 },
          },
        },
      },
    },
    // Campi alti 48 sui dispositivi touch: 40px erano stretti per il dito, e non
    // lasciavano spazio agli indicatori interni (erano fermi a 28x28).
    MuiInputBase: {
      styleOverrides: {
        root: {
          '@media (pointer: coarse)': { minHeight: 48 },
        },
        // le aree di testo multiriga crescono da sole: nessuna altezza minima
        multiline: {
          '@media (pointer: coarse)': { minHeight: 0 },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: {
          '@media (pointer: coarse)': { minWidth: 40, minHeight: 40 },
        },
        clearIndicator: {
          '@media (pointer: coarse)': { minWidth: 40, minHeight: 40 },
        },
      },
    },
    // Su schermo stretto la barra di paginazione non andava a capo: etichetta,
    // selettore e frecce stavano su una riga sola e la freccia "pagina successiva"
    // finiva FUORI dallo schermo (tagliata di 12px su Cantieri, Veicoli, Utenti,
    // Interventi). Qui una volta per tutte, invece che pagina per pagina.
    MuiTablePagination: {
      styleOverrides: {
        toolbar: {
          '@media (max-width:599.95px)': {
            paddingLeft: 8,
            paddingRight: 8,
            flexWrap: 'wrap',
            justifyContent: 'center',
            rowGap: 4,
          },
        },
        spacer: {
          '@media (max-width:599.95px)': { flex: 'none' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          // "Dettagli" nel Registro Attività era alto 24px
          '@media (pointer: coarse)': { minHeight: 48 },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
          '@media (pointer: coarse)': { minHeight: 40 },
        },
        contained: {
          boxShadow: '0 2px 8px rgba(253, 0, 0, 0.25)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(253, 0, 0, 0.35)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#fd0000',
            color: '#ffffff',
            fontWeight: 700,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
        // i chip cliccabili (filtri "Oggi", ordinamento in Interventi) sono
        // bersagli tattili a tutti gli effetti
        clickable: {
          '@media (pointer: coarse)': { minHeight: 40 },
        },
      },
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
          <Router>
            <SyncManager />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<RoleBasedRedirect />} />
                <Route path="dashboard" element={<DashboardProtected />} />
                <Route path="clienti" element={<Clienti />} />
                <Route path="clienti/:id" element={<ClienteDettaglio />} />
                <Route path="cantieri" element={<Cantieri />} />
                <Route path="cantieri/:id" element={<CantiereDettaglio />} />
                <Route path="interventi" element={<Interventi />} />
                <Route path="interventi/:id" element={<InterventoDettaglio />} />
                <Route path="calendario" element={<Calendario />} />
                <Route path="timbrature" element={<Timbrature />} />
                <Route path="rapportini" element={<Rapportini />} />
                <Route path="utenti" element={<Utenti />} />
                <Route path="veicoli" element={<Veicoli />} />
                <Route path="manuale" element={<Manuale />} />
                <Route path="profilo" element={<Profilo />} />
                <Route path="impostazioni" element={<Impostazioni />} />
                <Route path="registro-attivita" element={<RegistroAttivita />} />
              </Route>
            </Routes>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;